import React, { useState, useRef, useEffect, KeyboardEvent } from "react";
import { Search, X } from "lucide-react";
import { motion } from "framer-motion";
import { createPortal } from "react-dom";
// Import centralized icon definitions
import { EXTENDED_ICON_MAP, ICON_OPTIONS } from "../../config/icons"; // Corrected path

export interface IconSelectorProps {
  value: string;
  onChange: (value: string) => void;
  portalRoot?: HTMLElement | null;
  disabled?: boolean;
  className?: string;
}

const IconSelector: React.FC<IconSelectorProps> = ({
  value,
  onChange,
  portalRoot,
  disabled = false,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const iconsContainerRef = useRef<HTMLDivElement>(null);

  // Filter icons based on search query
  const filteredIcons = searchQuery
    ? ICON_OPTIONS.filter(icon => 
        icon.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        icon.value.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : ICON_OPTIONS;

  // Get the current selected icon component
  const SelectedIcon = EXTENDED_ICON_MAP[value] || EXTENDED_ICON_MAP.box;

  // Handle opening the dropdown
  const handleOpen = () => {
    if (disabled) return;
    setIsOpen(true);
    // Focus the search input when dropdown opens
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 10);
  };

  // Handle closing the dropdown
  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery("");
  };

  // Handle icon selection
  const handleSelectIcon = (iconValue: string) => {
    // Ensure we're actually changing the value
    if (iconValue !== value) {
      onChange(iconValue);
    }
    handleClose();
  };

  // Handle keyboard events for search field
  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      handleClose();
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Calculate dropdown position
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isOpen]);

  // Render the dropdown content
  const renderDropdown = () => {
    if (!isOpen) return null;

    const dropdownContent = (
      <div
        ref={dropdownRef}
        className="absolute z-50 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg overflow-hidden"
        style={{
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          maxHeight: '400px', // Set a max height
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Search field */}
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-700 shadow-sm">
          <div className="relative">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search icons..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="w-full pl-8 pr-6 py-2 text-sm border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            <Search className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
            {searchQuery && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                onClick={() => setSearchQuery("")}
                className="absolute right-4 top-2 h-3 w-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                aria-label="Clear search"
              >
                <X />
              </motion.button>
            )}
          </div>
        </div>

        {/* Icons grid - with explicit overflow-y: auto */}
        <div 
          ref={iconsContainerRef}
          className="grid grid-cols-6 gap-2 p-2 overflow-y-auto"
          style={{ 
            overflowY: 'auto',
            maxHeight: '300px',
            flex: '1 1 auto'
          }}
        >
          {filteredIcons.map((iconOption) => {
            const IconComponent = iconOption.icon;
            return (
              <button
                key={iconOption.value}
                type="button"
                onClick={() => handleSelectIcon(iconOption.value)}
                className={`flex items-center justify-center p-2 rounded-md transition-all ${
                  value === iconOption.value
                    ? "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 ring-2 ring-blue-500 dark:ring-blue-600"
                    : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800"
                }`}
                title={iconOption.label}
              >
                <IconComponent size={18} />
              </button>
            );
          })}
        </div>
      </div>
    );

    // Use portal if available, otherwise render in place
    return portalRoot ? createPortal(dropdownContent, portalRoot) : dropdownContent;
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Selected icon button */}
      <button
        type="button"
        onClick={handleOpen}
        //w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out
        className={`flex items-center justify-between w-full pl-3 pr-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 rounded ${
          disabled
            ? "opacity-60 cursor-not-allowed bg-gray-100 dark:bg-gray-800"
            : "focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
        }`}
        disabled={disabled}
      >
        <div className="flex items-center">
          <div className="w-6 h-6 flex items-center justify-center mr-2 text-gray-600 dark:text-gray-300">
            <SelectedIcon size={18} />
          </div>
          <span className="text-gray-700 dark:text-gray-300">
            {ICON_OPTIONS.find(icon => icon.value === value)?.label || "Select Icon"}
          </span>
        </div>
        <svg
          className="w-4 h-4 text-gray-500 dark:text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown */}
      {renderDropdown()}
    </div>
  );
};

export default IconSelector;
